import {
  tInteractiveButtonAction,
  tInteractiveCTAUrlAction,
  tInteractiveContent,
  tInteractiveListAction,
  tInteractiveOutbound,
  tInteractiveTextHeader,
  tInteractiveType,
  tQuickReplyInteractiveConfig,
  tWapiSendInteractiveContent,
} from '../../../../Chat.types';
import { LOCKED_FOOTER_TEXT } from 'src/constants/whatsappInteractive.constants';

// Re-exported for existing imports (CommonFields, tests) — AGI-73 hoisted the
// canonical value to a shared module so the sandbox can import it without
// pulling in this file's composer/Formik-adjacent dependency tree.
export { LOCKED_FOOTER_TEXT };

// Default empty action payload per sub-type. Used when the composer opens and
// when the seller switches type mid-compose (reset `action` so the union
// stays valid).
export const EMPTY_ACTIONS: {
  button: tInteractiveButtonAction;
  cta_url: tInteractiveCTAUrlAction;
  list: tInteractiveListAction;
} = {
  button: { buttons: [{ id: '', title: '' }] },
  cta_url: { display_text: '', url: '' },
  list: {
    button_text: '',
    sections: [{ title: undefined, rows: [{ id: '', title: '' }] }],
  },
};

// Returns the footer that should actually go on the wire given the user's
// plan. Used both at the UX boundary (CommonFields) and at the translator
// boundary (toMetaWireInteractive, toQuickReplyInteractiveConfig) so the
// coercion is belt-and-suspenders — a stale useEffect can't let a custom
// footer slip past on send.
export function coerceFooterForPlan(
  footer: string | undefined,
  canEditFooter: boolean,
): string | undefined {
  if (canEditFooter) return footer;
  return LOCKED_FOOTER_TEXT;
}

// Fresh composer values. When a preload matches a variant we narrow via a
// type guard so the action payload keeps its concrete shape; any stale
// field that can't exist on the target variant (e.g. rich header on a
// list) is dropped.
export function buildInitialInteractiveValues(
  overrides?: tInteractiveOutbound,
): tInteractiveOutbound {
  const type: tInteractiveType = overrides?.type ?? 'button';
  const body = overrides?.body ?? '';
  const footer = overrides?.footer;

  if (type === 'button') {
    const match = overrides?.type === 'button' ? overrides : undefined;
    return {
      type: 'button',
      body,
      footer,
      header: match?.header,
      action: match?.action ?? EMPTY_ACTIONS.button,
    };
  }
  if (type === 'cta_url') {
    const match = overrides?.type === 'cta_url' ? overrides : undefined;
    return {
      type: 'cta_url',
      body,
      footer,
      header: match?.header,
      action: match?.action ?? EMPTY_ACTIONS.cta_url,
    };
  }
  const match = overrides?.type === 'list' ? overrides : undefined;
  return {
    type: 'list',
    body,
    footer,
    header: match?.header?.type === 'text' ? match.header : undefined,
    action: match?.action ?? EMPTY_ACTIONS.list,
  };
}

// UTF-16 code-unit counter. Matches JS `.length` and Meta's server-side count
// (Meta's docs say "characters", they mean UTF-16 code units). 🙂 = 2 units,
// 👋🏽 = 4, 👨‍👩‍👧 = 11. Use everywhere a Meta limit is enforced — never
// grapheme count (`[...s].length`) because that diverges from server.
export function utf16Count(value: string | undefined | null): number {
  return (value ?? '').length;
}

// Host classification for CTA URL safety. Kept separate from scheme check so
// the checks compose: e.g., a `javascript:` URL has no host and should fail at
// scheme step before host-parsing is attempted.
const PRIVATE_HOSTS_EXACT = new Set([
  'localhost',
  'ip6-localhost',
  'ip6-loopback',
  'metadata.google.internal',
]);

function isPrivateIPv4(host: string): boolean {
  const octets = host.split('.').map((o) => Number(o));
  if (octets.length !== 4 || octets.some((o) => Number.isNaN(o) || o < 0 || o > 255)) {
    return false;
  }
  const [a, b] = octets;
  if (a === 10) return true;
  if (a === 127) return true;
  if (a === 169 && b === 254) return true; // link-local + AWS/GCP/Azure IMDS (169.254.169.254)
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 100 && b >= 64 && b <= 127) return true; // CGNAT 100.64.0.0/10
  if (a === 0) return true; // 0.0.0.0/8 unspecified
  return false;
}

function isPrivateIPv6(host: string): boolean {
  const h = host.toLowerCase();
  if (h === '::1' || h === '::' || h === '[::1]' || h === '[::]') return true;
  // fc00::/7 unique local
  if (h.startsWith('fc') || h.startsWith('fd')) return true;
  // fe80::/10 link-local
  if (h.startsWith('fe8') || h.startsWith('fe9') || h.startsWith('fea') || h.startsWith('feb')) {
    return true;
  }
  // IPv4-mapped (::ffff:127.0.0.1)
  if (h.includes('ffff:') && /(\d{1,3}\.){3}\d{1,3}/.test(h)) {
    const ipv4 = h.split(':').slice(-1)[0];
    if (ipv4 && isPrivateIPv4(ipv4.replace(/^\[|\]$/g, ''))) return true;
  }
  return false;
}

export type tUrlSafetyResult =
  | { ok: true }
  | { ok: false; reason: 'scheme' | 'host' | 'length' | 'parse' | 'userinfo' };

// Returns the raw URL when it passes `checkCtaUrlSafety`, otherwise null.
// Use at render time for any URL read off stored/replayed interactive
// payloads (header.image.link, header.document.link, action.url). The Yup
// schema validates on the way out but a tampered cache, compromised service
// writing to chat_events, or malicious drag-and-drop can bypass that — so
// every consumer re-checks before wiring into href= / src=.
export function safeInteractiveUrl(
  raw: string | undefined | null,
): string | null {
  if (!raw) return null;
  return checkCtaUrlSafety(raw).ok ? raw : null;
}

export function checkCtaUrlSafety(raw: string): tUrlSafetyResult {
  if (!raw) return { ok: false, reason: 'parse' };
  if (raw.length > 2048) return { ok: false, reason: 'length' };

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return { ok: false, reason: 'parse' };
  }

  // HTTPS only. Rejects javascript:, data:, file:, tel:, http:, ftp:, etc.
  if (parsed.protocol !== 'https:') return { ok: false, reason: 'scheme' };

  // Reject URLs with userinfo (user:pass@host). These can be used to confuse
  // victims about the real host.
  if (parsed.username || parsed.password) return { ok: false, reason: 'userinfo' };

  // Host safety — normalize and screen.
  const hostname = parsed.hostname.toLowerCase();
  if (!hostname) return { ok: false, reason: 'host' };
  if (PRIVATE_HOSTS_EXACT.has(hostname)) return { ok: false, reason: 'host' };
  if (isPrivateIPv4(hostname)) return { ok: false, reason: 'host' };
  if (isPrivateIPv6(hostname)) return { ok: false, reason: 'host' };

  // Reject single-label hostnames (e.g. `https://leadsales/io` — typo for
  // `https://leadsales.io`). The browser's URL parser happily accepts them
  // because they're syntactically legal, but WBS's authoritative `safeurl`
  // validator rejects them at send time (DNS lookup fails) and the user
  // sees a generic VALIDATION_ERROR with no field context. Catch it here
  // with a specific Yup error so the typo is surfaced at the field. IP
  // literals are already screened above, so they don't need the dot.
  if (!hostname.includes('.')) return { ok: false, reason: 'host' };

  return { ok: true };
}

// Per Meta spec: list headers are text-only. The clean shape encodes this at
// the type level (`tInteractiveTextHeader` on the list variant, richer
// `tInteractiveHeader` on button/cta_url). Union narrowing via `if
// (header.type === 'text')` isolates the media-only fields cleanly.
function toMetaHeader(
  header: tInteractiveOutbound['header'],
): tWapiSendInteractiveContent['header'] | undefined {
  if (!header) return undefined;
  if (header.type === 'text') {
    return { type: 'text', text: header.text };
  }
  const out: tWapiSendInteractiveContent['header'] = { type: header.type };
  if (header.text !== undefined) out.text = header.text;
  if (header.image !== undefined) out.image = header.image;
  if (header.video !== undefined) out.video = header.video;
  if (header.document !== undefined) out.document = header.document;
  return out;
}

// Translate the authoring discriminated union to Meta's polymorphic wire shape
// that bff-web PR #643 expects. Front-leadsales composes in the clean shape;
// this function is the single bridge to Meta vocabulary. Pass
// `options.canEditFooter = false` for Pro/Basic plans so the footer is
// coerced to LOCKED_FOOTER_TEXT at the wire boundary regardless of what
// Formik state happens to contain.
export function toMetaWireInteractive(
  content: tInteractiveOutbound,
  options: { canEditFooter?: boolean } = {},
): tWapiSendInteractiveContent {
  const canEditFooter = options.canEditFooter ?? true;
  const footer = coerceFooterForPlan(content.footer, canEditFooter);
  const base: Omit<tWapiSendInteractiveContent, 'action'> = {
    type: content.type,
    body: { text: content.body },
    ...(footer ? { footer: { text: footer } } : {}),
    ...(content.header ? { header: toMetaHeader(content.header) } : {}),
  };

  switch (content.type) {
    case 'button':
      return {
        ...base,
        action: {
          buttons: content.action.buttons.map((b) => ({
            type: 'reply',
            reply: { id: b.id, title: b.title },
          })),
        },
      };
    case 'cta_url':
      return {
        ...base,
        action: {
          name: 'cta_url',
          parameters: {
            display_text: content.action.display_text,
            url: content.action.url,
          },
        },
      };
    case 'list':
      return {
        ...base,
        // Meta's key is `button`; our clean shape uses `button_text`.
        action: {
          button: content.action.button_text,
          sections: content.action.sections.map((s) => ({
            ...(s.title ? { title: s.title } : {}),
            rows: s.rows.map((r) => ({
              id: r.id,
              title: r.title,
              ...(r.description ? { description: r.description } : {}),
            })),
          })),
        },
      };
  }
}

// Translates the Formik composer draft to quick-replies-service's
// `InteractiveConfig` wire shape (see quick-replies-service PR #45). Key
// differences from the composer shape:
//   - no top-level `action` — the variant payload lives under
//     `button` / `cta_url` / `list` instead.
//   - `footer` is a plain string, not `{ text }`.
// Footer is coerced via `coerceFooterForPlan` — a saved quick reply for a
// non-Advanced workspace should store the locked footer, not whatever
// Formik state happens to have mid-transition.
export function toQuickReplyInteractiveConfig(
  draft: tInteractiveOutbound,
  options: { canEditFooter?: boolean } = {},
): tQuickReplyInteractiveConfig {
  const canEditFooter = options.canEditFooter ?? true;
  const footer = coerceFooterForPlan(draft.footer, canEditFooter);
  const base: tQuickReplyInteractiveConfig = {
    type: draft.type,
    body: draft.body,
    ...(footer ? { footer } : {}),
    ...(draft.header ? { header: draft.header } : {}),
  };

  switch (draft.type) {
    case 'button':
      return { ...base, button: draft.action };
    case 'cta_url':
      return { ...base, cta_url: draft.action };
    case 'list':
      return { ...base, list: draft.action };
  }
}

// Inverse of toQuickReplyInteractiveConfig — reads the
// quick-replies-service wire shape (flat `button` / `cta_url` / `list`
// keys) and produces the composer's `tInteractiveOutbound` (nested under
// `action`). Used when hydrating GET /quick-replies responses so the rest
// of the UI can treat interactive and text replies uniformly via
// `content.interactive`. Returns `null` when the input can't be
// narrowed to a known variant (missing action payload or unknown type).
export function fromQuickReplyInteractiveConfig(
  config: tQuickReplyInteractiveConfig | null | undefined,
): tInteractiveOutbound | null {
  if (!config) return null;
  if (config.type === 'button' && config.button) {
    return {
      type: 'button',
      body: config.body,
      ...(config.footer ? { footer: config.footer } : {}),
      ...(config.header ? { header: config.header } : {}),
      action: config.button,
    };
  }
  if (config.type === 'cta_url' && config.cta_url) {
    return {
      type: 'cta_url',
      body: config.body,
      ...(config.footer ? { footer: config.footer } : {}),
      ...(config.header ? { header: config.header } : {}),
      action: config.cta_url,
    };
  }
  if (config.type === 'list' && config.list) {
    // `list` variant restricts header to text-only per Meta spec; drop
    // anything else rather than crash the type guard on read. Explicit
    // narrow since the text-only header type is strictly tighter than
    // the shared tInteractiveHeader used on button/cta_url variants.
    const header: tInteractiveTextHeader | undefined =
      config.header?.type === 'text' && typeof config.header.text === 'string'
        ? { type: 'text', text: config.header.text }
        : undefined;
    return {
      type: 'list',
      body: config.body,
      ...(config.footer ? { footer: config.footer } : {}),
      ...(header ? { header } : {}),
      action: config.list,
    };
  }
  return null;
}

// Type guard for reading from ChatEventPg.content.interactive on inbound rows.
// Used by the EventBubble dispatcher to pick InteractiveReplyBubble.
export function isInteractiveReply(
  interactive: tInteractiveContent | undefined,
): interactive is Extract<
  tInteractiveContent,
  { type: 'button_reply' | 'list_reply' | 'flow_reply' }
> {
  return (
    interactive !== undefined &&
    (interactive.type === 'button_reply' ||
      interactive.type === 'list_reply' ||
      interactive.type === 'flow_reply')
  );
}

// Type guard for outbound interactive content on stored chat events. Mirrors
// isInteractiveReply — the EventBubble dispatcher routes outbound interactive
// messages (button / cta_url / list) to the dedicated
// OutboundInteractiveBubble instead of the generic text renderer, which only
// surfaces `content.text` and would hide the action buttons entirely.
export function isInteractiveOutbound(
  interactive: tInteractiveContent | undefined,
): interactive is Extract<
  tInteractiveContent,
  { type: 'button' | 'cta_url' | 'list' }
> {
  return (
    interactive !== undefined &&
    (interactive.type === 'button' ||
      interactive.type === 'cta_url' ||
      interactive.type === 'list')
  );
}

// Total row count across all sections — used by ListBuilder's send gate and
// by the Yup schema. Meta caps total rows at 10 across ALL sections.
export function totalListRowCount(
  sections: { rows: unknown[] }[] | undefined,
): number {
  if (!sections) return 0;
  return sections.reduce((sum, section) => sum + section.rows.length, 0);
}

// Auto-generated `id` from a button/row title. Bots + analytics need a stable
// string per option; sellers shouldn't have to hand-type one. The slug is
// ASCII-only (strips accents + non-alphanumerics → kebab-case) so it round-
// trips cleanly through Meta's webhook payload. If the seller cares about a
// specific id shape (bot correlation with an existing state machine), the
// "Avanzado" panel in the builder lets them override — we stop auto-syncing
// once they've touched the field.
export function slugifyId(title: string | undefined | null): string {
  if (!title) return '';
  return (
    title
      // Strip combining marks (á → a, ñ → n) via NFD decomposition.
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim()
      // Replace runs of non-alphanumerics with a single hyphen.
      .replace(/[^a-z0-9]+/g, '-')
      // Trim leading/trailing hyphens.
      .replace(/^-+|-+$/g, '')
      // Cap at 64 chars — Meta allows 256 for buttons / 200 for list rows,
      // but shorter ids are easier to debug in logs + webhooks.
      .slice(0, 64)
  );
}
